import React, { useState } from 'react';
import axios from 'axios';
import validator from 'validator';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import useReducedMotion from '../../hooks/useReducedMotion';

const CONTACT_ENDPOINT = 'https://d2bssy5n2iokjx4q4ihxwc2zxy0jjinp.lambda-url.us-east-1.on.aws/';
const CALM_VIDEO_SRC = '/media/clip-calm.mp4';
const CALM_POSTER_SRC = '/media/calm-poster.webp';

export default function FinalCta() {
  const prefersReducedMotion = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  const showVideo = !prefersReducedMotion && !videoFailed;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setError('Name, email, and message are required.');
      setStatus('error');
      return;
    }

    if (!validator.isEmail(trimmedEmail)) {
      setError('Please enter a valid work email.');
      setStatus('error');
      return;
    }

    setError('');
    setStatus('sending');

    try {
      await axios.post(CONTACT_ENDPOINT, {
        name: trimmedName,
        email: trimmedEmail,
        message: trimmedMessage,
        source: 'phennx.io/contact',
      });

      setStatus('success');
      setName('');
      setEmail('');
      setMessage('');
    } catch (err) {
      setStatus('error');
      setError('Could not send message. Please try again or email info@phennx.io.');
    }
  };

  const isSending = status === 'sending';
  const isSuccess = status === 'success';
  const isError = status === 'error';
  const errorId = 'contactError';

  return (
    <section className="hx-cta" id="contact" aria-labelledby="cta-heading">
      <div className="hx-cta__media" aria-hidden="true">
        {showVideo ? (
          <video
            className="hx-cta__video"
            src={CALM_VIDEO_SRC}
            poster={CALM_POSTER_SRC}
            muted
            playsInline
            loop
            autoPlay
            preload="metadata"
            tabIndex={-1}
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <img className="hx-cta__video" src={CALM_POSTER_SRC} alt="" loading="lazy" />
        )}
        <div className="hx-cta__scrim" />
      </div>

      <div className="hx-container hx-cta__inner">
        <div className="hx-cta__pitch">
          <p className="hx-eyebrow">
            <span className="hx-eyebrow__tick" aria-hidden="true" />
            Request a demo
          </p>
          <h2 className="hx-cta__title" id="cta-heading">
            Run the next study
            <br />
            with less drift.
          </h2>
          <p className="hx-lead">
            Tell us about the studies you run. We&rsquo;ll walk you through the platform and map it to
            your workflows — no slideware, the real product.
          </p>
          <p className="hx-cta__alt">
            Prefer LinkedIn? Message{' '}
            <a
              className="hx-link"
              href="https://www.linkedin.com/company/phennx"
              target="_blank"
              rel="noreferrer"
            >
              Phennx on LinkedIn
              <span className="hx-visually-hidden"> (opens in a new tab)</span>
            </a>
            .
          </p>
        </div>

        <form className="hx-cta__form" onSubmit={handleSubmit} noValidate>
          <div className="hx-field">
            <label className="hx-field__label" htmlFor="contactName">
              Full name
            </label>
            <InputText
              id="contactName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full"
              autoComplete="name"
              required
              aria-required="true"
              aria-invalid={isError}
              aria-describedby={isError ? errorId : undefined}
              disabled={isSending}
            />
          </div>

          <div className="hx-field">
            <label className="hx-field__label" htmlFor="contactEmail">
              Work email
            </label>
            <InputText
              id="contactEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full"
              inputMode="email"
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={isError}
              aria-describedby={isError ? errorId : undefined}
              disabled={isSending}
            />
          </div>

          <div className="hx-field">
            <label className="hx-field__label" htmlFor="contactMessage">
              Message
            </label>
            <InputTextarea
              id="contactMessage"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What kind of trials do you run, and what are you hoping to streamline?"
              className="w-full"
              autoResize
              rows={4}
              required
              aria-required="true"
              aria-invalid={isError}
              aria-describedby={isError ? errorId : undefined}
              disabled={isSending}
            />
          </div>

          <div className="hx-cta__actions">
            <Button
              type="submit"
              label={isSending ? 'Sending...' : 'Request a demo'}
              icon={isSending ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
              className="hx-btn hx-btn--primary"
              loading={isSending}
              disabled={isSending}
            />
            <Button
              label="Compliance Center"
              icon="pi pi-shield"
              className="hx-btn hx-btn--ghost"
              type="button"
              onClick={() => window.open('https://compliance.phennx.io', '_blank', 'noopener,noreferrer')}
              aria-label="Compliance Center (opens in a new tab)"
            />
          </div>

          {isError && (
            <p className="hx-cta__error" id={errorId} role="alert">
              {error}
            </p>
          )}
          {isSuccess && (
            <p className="hx-cta__success" role="status" aria-live="polite">
              Thanks! We received your message and will reach out with next steps.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
